import React, { FormEvent, useState, useMemo } from 'react';
import { Link, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { useMultistepForm } from '../EsSignupForm/useMultistepForm';
import { EsVerifyCode } from '../EsVerify/EsVerifyCode';
import { EsSigninData, TUsernamePassword } from './EsSigninData';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { validate } from '../../utils/validators';
import { strings } from '../../constants/strings';
import { isEmpty } from '../../utils/lodash';
import CustomButton from '../Button/CustomButton';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';
import { OTPPage } from '../EsSignupForm/OTPPage';

const LOGIN_DATA: TUsernamePassword = {
    email: '',
    password: '',
};

interface ILoginProps {
    onSubmit: (data: TUsernamePassword, callBack?: () => void, isLastStep?: boolean) => void;
    onResendOtp: (data: TUsernamePassword) => Promise<unknown>;
    loading?: boolean;
    otpLoading?: boolean;
    resendInProgress?: boolean;
    isModalMode?: boolean;
    onSignUpClick?: VoidFunction;
    formStyle?: React.CSSProperties;
    loginMessage?: string;
    loginChannel?: 'sms' | 'whatsapp' | 'audme' | 'email';
    audmeData?: {
        token?: string;
        qrCode?: string;
        deeplink?: string;
    } | null;
}

const EsLoginForm: React.FC<ILoginProps> = ({
    onSubmit,
    onResendOtp,
    loading,
    otpLoading,
    resendInProgress,
    onSignUpClick,
    isModalMode,
    formStyle,
    loginMessage,
    loginChannel,
    audmeData,
}) => {
    const [data, setData] = useState(LOGIN_DATA);
    const [otp, setOtp] = useState<string[]>();
    const [showAlternateMethod, setShowAlternateMethod] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState<'sms' | 'whatsapp' | 'audme' | 'email' | undefined>(
        loginChannel,
    );
    const [selectingChannel, setSelectingChannel] = useState<boolean>(false);
    const { themeStyle, device } = useThemeContext();

    // Update selectedChannel when loginChannel prop changes
    React.useEffect(() => {
        setSelectedChannel(loginChannel);
    }, [loginChannel]);

    function updateFields(fields: Partial<TUsernamePassword>) {
        setData(prev => {
            return { ...prev, ...fields };
        });
    }

    const enableButton = () => {
        return !isEmpty(data.email) && validate.validateEmail(data.email) && !isEmpty(data.password);
    };

    const handleAlternateMethodClick = () => {
        setShowAlternateMethod(true);
    };

    const handleAlternateMethodSelection = (channel: 'sms' | 'whatsapp' | 'audme' | 'email') => {
        // Update the channel immediately
        setSelectingChannel(true);
        setSelectedChannel(channel);
        // Call login again with the selected verificationChannel
        // Explicitly construct payload to ensure verificationChannel is included
        const payload: TUsernamePassword = {
            email: data.email,
            password: data.password,
            token: data.token,
            verificationChannel: channel,
        };
        onSubmit(
            payload,
            () => {
                setShowAlternateMethod(false);
                setSelectingChannel(false);
            },
            false,
        );
    };

    const stepsArray = useMemo(() => {
        const baseSteps = [<EsSigninData {...data} updateFields={updateFields} />];

        if (showAlternateMethod) {
            // Show OTP selection page
            baseSteps.push(
                <OTPPage
                    key="otp-selection"
                    stepIndex={1}
                    totalSteps={2}
                    title="Select OTP Preference"
                    subtitle="Choose where you would like to receive your security code."
                    phoneNumber={data.email}
                    email={data.email}
                    onSetOTPPreference={handleAlternateMethodSelection}
                    loading={selectingChannel}
                    audmeData={audmeData}
                    onBack={() => {
                        setShowAlternateMethod(false);
                        back();
                    }}
                />,
            );
        } else {
            // Show verify code step
            baseSteps.push(
                <EsVerifyCode
                    key={`verify-code-${selectedChannel || 'default'}`}
                    {...data}
                    verificationChannel={loginChannel}
                    onClickBack={() => back()}
                    onChange={(otp: string[]) => setOtp(otp)}
                    onResendOtp={() => onResendOtp(data)}
                    loading={otpLoading}
                    resendInProgress={resendInProgress}
                    subtitleMessage={loginMessage}
                    onAlternateMethodClick={handleAlternateMethodClick}
                    audmeData={audmeData}
                />,
            );
        }

        return baseSteps;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, selectedChannel, loginMessage, showAlternateMethod, otpLoading, resendInProgress]);

    const { step, isFirstStep, isLastStep, next, back } = useMultistepForm(stepsArray);

    const handleSubmitProps = () => {
        const payload = { ...data, code: otp?.join('') };
        onSubmit(payload, next, isLastStep);
        MixPanel.track(MIXPANEL_EVENT.LOGIN, { buttonAction: 'LOGIN', buttonName: 'SIGN IN' });
    };

    function onSubmitHandler(e: FormEvent) {
        e.preventDefault();
        handleSubmitProps();
    }

    return (
        <form
            style={{
                width: '100%',
                maxWidth: 500,
                boxSizing: 'border-box',
                padding: device.isMobile ? 20 : 50,
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: theme[themeStyle].form_black,
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme[themeStyle].pure_black,
                ...formStyle,
            }}
            onSubmit={onSubmitHandler}>
            {step}
            {isFirstStep && (
                <>
                    <CustomButton
                        label={strings.sign_in}
                        loading={loading}
                        buttonProps={{ type: 'submit' }}
                        disabled={!enableButton() || loading}
                        customStyle={{ width: '100%', borderRadius: '50px' }}
                        sx={{ '&:hover': { WebkitTextFillColor: theme[themeStyle].primary_orange } }}
                    />
                    <Link
                        href={isModalMode ? undefined : '/signup'}
                        sx={{
                            '&:hover': { cursor: 'pointer' },
                            textDecoration: 'none',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <CustomButton
                            onClick={onSignUpClick}
                            label="Sign Up"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                color: theme[themeStyle].tag_white,
                                textAlign: 'center',
                                marginTop: 2,
                                width: '100%',
                                border: `0.5px solid ${theme[themeStyle].highlight}`,
                                backgroundColor: 'transparent',
                                maxWidth: 400,
                                borderRadius: 50,
                                padding: 12,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'transparent' },
                            }}
                            textStyle={{
                                color: theme[themeStyle].tag_white,
                            }}
                            customStyle={{
                                borderRadius: 50,
                            }}
                            textSx={{
                                color: theme[themeStyle].tag_white,
                                fontSize: 14,
                                '&:hover': { color: theme[themeStyle].black },
                            }}
                        />
                    </Link>
                </>
            )}
            {isFirstStep && (
                <Typography
                    style={{
                        width: '100%',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        color: theme[themeStyle].tag_white,
                        marginTop: 20,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        alignSelf: 'center',
                    }}>
                    By clicking Sign in, you agree to our terms, Privacy Policy and Cookies Policy. You may recieve SMS
                    notifications from us and can opt out at any time. {strings.protected_page}
                    <a
                        href="https://www.google.com/recaptcha/about/"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            width: '100%',
                            fontSize: 14,
                            fontStyle: 'normal',
                            fontWeight: '400',
                            marginTop: 40,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].main_blue,
                            cursor: 'pointer',
                            marginLeft: '8px',
                        }}>
                        {strings.learn_more}
                    </a>
                </Typography>
            )}
        </form>
    );
};

export { EsLoginForm };
